import React from 'react';

import type { UUID } from '@aline/common';
import { Select } from '@aline/common';
import { link } from '@aline/routes';
import { Layers, Rocket } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AlineButton } from 'src/components/generic';
import { Apollo } from 'src/gql/generated';
import { useGetWorkflowSimple, useTogglePublishTemplate } from 'src/hooks/workflow/workflow';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  actions: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  label: {
    flex: 1,
    fontWeight: theme.typography.fontWeightBold,
  },
  root: {
    alignItems: 'center',
    backgroundColor: '#E1E4E5',
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 2),
    width: '100%',
  },
}));

interface WorkflowTemplateToolbarProps {
  workflowId: UUID;
  readOnly?: boolean;
}

export function WorkflowTemplateToolbar({
  readOnly = false,
  workflowId,
}: WorkflowTemplateToolbarProps) {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { workflow } = useGetWorkflowSimple(workflowId);
  const { loading: publishLoading, togglePublishTemplate } = useTogglePublishTemplate(workflowId);

  if (!workflow || workflow.variant !== Apollo.WorkflowVariant.Template) return null;

  const isPublished = Boolean(workflow.publishedAt);

  return (
    <div className={classes.root}>
      <Layers size={16} />
      <span className={classes.label}>{isPublished ? 'PUBLISHED TEMPLATE' : 'DRAFT TEMPLATE'}</span>
      <div className={classes.actions}>
        <ToolbarActions
          isPublished={isPublished}
          publishLoading={publishLoading}
          readOnly={readOnly}
          onLaunch={() =>
            navigate(
              link.QuickStartLaunchTemplateSelectedTemplate({
                templateWorkflowId: workflowId,
              })
            )
          }
          onTogglePublish={() => void togglePublishTemplate()}
        />
      </div>
    </div>
  );
}

function ToolbarActions({
  isPublished,
  onLaunch,
  onTogglePublish,
  publishLoading,
  readOnly,
}: {
  isPublished: boolean;
  onLaunch: () => void;
  onTogglePublish: () => void;
  publishLoading: boolean;
  readOnly: boolean;
}) {
  if (isPublished) {
    return (
      <>
        <AlineButton
          color="primary"
          disabled={readOnly}
          loading={publishLoading}
          size="small"
          variant="outlined"
          onClick={onTogglePublish}
        >
          Unpublish
        </AlineButton>
        <AlineButton
          color="primary"
          data-cy={Select.workflow.detail.toolbar.launchWorkflowButton}
          disabled={readOnly}
          size="small"
          startIcon={<Rocket size={14} />}
          variant="contained"
          onClick={onLaunch}
        >
          Launch
        </AlineButton>
      </>
    );
  }

  return (
    <AlineButton
      color="primary"
      disabled={readOnly}
      loading={publishLoading}
      size="small"
      variant="contained"
      onClick={onTogglePublish}
    >
      Publish
    </AlineButton>
  );
}
